import { useLayoutEffect } from 'react';

import { useGlobalEvent } from '@luna/events';

const scrollToTop = (useTimeout = false) => {
    if (useTimeout) {
        setTimeout(() => window.scroll(0, 0), 0);
    } else {
        window.scroll(0, 0);
    }
};

/**
 * A custom React hook that scrolls to the top of the page on route changes
 * and component mount.
 *
 * This hook uses the `useGlobalEvent` hook to listen for route changes and
 * `useLayoutEffect` to scroll on component mount. It's useful for ensuring
 * that the page starts at the top when navigating between routes.
 *
 * @param {boolean} [useTimeout=false] - If true, the scroll action is deferred
 *                                       using setTimeout. This can be useful if you
 *                                       need to ensure that the DOM has updated
 *                                       before scrolling.
 *
 * @example
 * // Basic usage in a layout component
 * function Layout({ children }) {
 *   useScrollToTop();
 *
 *   return (
 *     <div>
 *       <Header />
 *       <main>{children}</main>
 *       <Footer />
 *     </div>
 *   );
 * }
 *
 */
export const useScrollToTop = (useTimeout = false): void => {
    useGlobalEvent('route:change', () => {
        scrollToTop(useTimeout);
    });

    useLayoutEffect(() => {
        scrollToTop(useTimeout);
    }, [useTimeout]);
};
